import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown } from "lucide-react";
import { usePortfolioStore } from "../store/portfolioStore";
import { useThemeStore } from "../store/themeStore";
import { formatCurrency } from "../lib/utils";

const MASK = "••••••";

const PortfolioSummary: React.FC = () => {
  const { exchangeRate, calculateSummary, getPortfolioCurrency, loading } =
    usePortfolioStore();
  const { maskAmounts } = useThemeStore();
  const summary = calculateSummary();
  const portfolioCurrency = getPortfolioCurrency();
  const m = (v: string) => (maskAmounts ? MASK : v);

  const summaryCards = useMemo(
    () => [
      {
        title: "Total Value",
        value: m(formatCurrency(summary.totalValue, portfolioCurrency)),
        subtitle: m(portfolioCurrency === "THB"
          ? `≈ ${formatCurrency(summary.totalValue / exchangeRate, "USD")}`
          : `≈ ${formatCurrency(summary.totalValue * exchangeRate, "THB")}`),
        color: "blue",
      },
      {
        title: "Total Invested",
        value: m(formatCurrency(summary.totalCost, portfolioCurrency)),
        subtitle: m(portfolioCurrency === "THB"
          ? `≈ ${formatCurrency(summary.totalCost / exchangeRate, "USD")}`
          : `≈ ${formatCurrency(summary.totalCost * exchangeRate, "THB")}`),
        color: "violet",
      },
      {
        title: "Unrealize P&L",
        value: m(`${summary.totalGainLoss >= 0 ? "+" : ""}${formatCurrency(summary.totalGainLoss, portfolioCurrency)}`),
        percentage: maskAmounts ? undefined : `${summary.totalGainLoss >= 0 ? "+" : ""}${summary.gainLossPercent.toFixed(2)}%`,
        subtitle: m(portfolioCurrency === "THB"
          ? `≈ ${formatCurrency(summary.totalGainLoss / exchangeRate, "USD")}`
          : `≈ ${formatCurrency(summary.totalGainLoss * exchangeRate, "THB")}`),
        color: summary.totalGainLoss >= 0 ? "green" : "red",
        isPositive: summary.totalGainLoss >= 0,
      },
      {
        title: "Realized P&L",
        value: m(`${summary.realizedPnL >= 0 ? "+" : ""}${formatCurrency(summary.realizedPnL, portfolioCurrency)}`),
        subtitle: m(portfolioCurrency === "THB"
          ? `≈ ${formatCurrency(summary.realizedPnL / exchangeRate, "USD")}`
          : `≈ ${formatCurrency(summary.realizedPnL * exchangeRate, "THB")}`),
        color: summary.realizedPnL >= 0 ? "green" : "red",
        isPositive: summary.realizedPnL >= 0,
      },
    ],
    [summary, portfolioCurrency, exchangeRate],
  );

  const tone: Record<string, string> = {
    blue: "stat-tile-primary",
    violet: "stat-tile-violet",
    green: "stat-tile-gain",
    red: "stat-tile-loss",
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {["stat-tile-primary", "stat-tile-violet", "stat-tile-gain", "stat-tile-gain"].map((t, i) => (
          <div key={i} className={`stat-tile ${t}`}>
            <div className="h-3 w-20 bg-white/60 dark:bg-white/10 rounded animate-pulse" />
            <div className="h-7 w-32 bg-white/60 dark:bg-white/10 rounded-lg animate-pulse" />
            <div className="h-3 w-24 bg-white/60 dark:bg-white/10 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {summaryCards.map((card) => {
        const t = tone[card.color] ?? tone.blue;
        return (
          <motion.div
            key={`summary-${card.title}`}
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            className={`stat-tile ${t}`}
          >
            <p
              className="stat-title"
            >
              {card.title}
            </p>

            <p
              className="stat-value"
            >
              {card.value}
            </p>

            <div className="flex items-center justify-between gap-2">
              <p className="stat-caption">
                {card.subtitle}
              </p>
              {card.percentage && (
                <span
                  className="stat-badge"
                >
                  {card.isPositive ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {card.percentage}
                </span>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default PortfolioSummary;
